import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  canonical, 
  ogType = 'website', 
  ogImage = 'https://answerly.space/og-image.png', // Default OG image
  keywords,
  schema,
  breadcrumbSteps = [] // [{ name: 'Home', path: '/' }]
}) => {
  const siteName = 'Answerly';
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  const url = canonical ? `https://answerly.space${canonical}` : 'https://answerly.space';

  // 1. Software Application Schema (Global Default)
  // No aggregateRating here on purpose - Answerly is pre-launch with zero
  // real reviews. Fabricated review counts in structured data are exactly
  // what Google's review-snippet spam policy targets, and it's a
  // whole-domain manual action risk, not a per-page one. Add this back
  // only once there's a real rating source to point at.
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Answerly",
    "operatingSystem": "Web",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "AI-powered resume builder and job application assistant that helps candidates pass ATS filters and land interviews."
  };

  // 2. Organization Schema (Global, every page) — "Answerly" is a genuinely
  // crowded name (an unrelated AI customer-support SaaS, an unrelated Canvas
  // homework-solver Chrome extension, and others all use it too). sameAs
  // gives Google an explicit, machine-readable signal for which real-world
  // entity answerly.space actually is, separate from and unaffected by the
  // nofollow links on the Chrome Web Store/App Store listings themselves
  // (verified: CWS's own outbound link to this site is rel="ugc nofollow" —
  // real and clickable, but deliberately not counted for ranking; sameAs
  // doesn't need a followed link to do its job). Only include URLs verified
  // to actually be this product's own listing — a wrong sameAs is actively
  // harmful, not neutral.
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Answerly",
    "alternateName": "Answerly – AI Job Application Assistant",
    "url": "https://answerly.space",
    "logo": "https://answerly.space/logo128.png",
    "description": "AI job application assistant — resume tailoring, autofill, and ATS matching for job seekers.",
    "sameAs": [
      "https://chromewebstore.google.com/detail/answerly-%E2%80%93-ai-job-applica/dfkdkenjpfdbkdieamadnbjolnmfnojp"
    ]
  };

  // 3. Breadcrumb Schema (Rich Results Friendly)
  const breadcrumbSchema = breadcrumbSteps.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbSteps.map((step, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": step.name,
      "item": `https://answerly.space${step.path}`
    }))
  } : null;

  return (
    <Helmet>
      {/* Standard metadata */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      {(Array.isArray(schema) ? schema : [schema || defaultSchema]).map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
